import { Box } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import DataTable, { DataTableFilter } from "../components/DataTable";
import Subheader from "../components/Subheader";
import Api from "../openapi";
import { AdminMealMultiLangResponse } from "../openapi/client";
import { GridPaginationModel, GridCallbackDetails, GridSortModel } from "@mui/x-data-grid";
import MealCrud from "../components/MealCrud";
import { generateRandom } from "../utils";
import useConfirm from "../hooks/useConfirm";
import { DeleteLabelIcon } from "../components/IconLabel";
import { useDispatch } from "react-redux";
import { setNotification } from "../store/notificationSlice";

interface IEditMeal {
  show: boolean;
  meal?: AdminMealMultiLangResponse;
}

export default function Meals() {
  const dispatch = useDispatch();
  const [meals, setMeals] = useState<AdminMealMultiLangResponse[]>([]);
  const [createShow, setCreateShow] = useState(false);
  const [editShow, setEditShow] = useState<IEditMeal>({ show: false, meal: undefined });
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [retriggerReq, setRetriggerReq] = useState<number>();
  const [filterModel, setFilterModel] = useState<DataTableFilter>({});
  const [ConfirmDialog, confirmDelete] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to permanently delete a meal from the list, and this action is irreversible. Are you sure you want to proceed?",
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: "name",
      sort: "asc",
    },
  ]);

  const handlePaginationChange = (model: GridPaginationModel, details: GridCallbackDetails<any>) => {
    setPaginationModel(model);
  };
  const handleSortChange = (sortModel: GridSortModel) => {
    sortModel.length && setSortModel(sortModel);
  };

  const create = () => {
    setCreateShow(true);
  };

  const edit = (params: any) => {
    // console.log("edit clicked", params);
    setEditShow({ show: true, meal: params.row });
  };
  const deleteRec = async (params: any) => {
    // console.log("delete clicked", params);
    const answer = await confirmDelete();
    if (!answer) return;

    try {
      const req = await Api.AdminMeal.deleteMeal(params.row.id);
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message: "Meal has been deleted successfully",
        })
      );
      setRetriggerReq(generateRandom());
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
  };

  const handleClose = () => {
    setCreateShow(false);
    setEditShow({ show: false, meal: undefined });
  };
  const handleConfirm = () => {
    // popup returns refresh status
    // update the meal list
    setRetriggerReq(generateRandom());
  };

  const columns = useMemo(() => {
    return [
      {
        field: "name",
        minWidth: 300,
        flex: 1,
        headerName: "Name",
        valueGetter: (params: any) => params.row.name.en,
      },
      {
        field: "portions.calories",
        minWidth: 80,
        flex: 1,
        headerName: "Calories",
        valueGetter: (params: any) => params.row.portions[0]?.calories,
      },
      {
        field: "portions.carbs",
        minWidth: 80,
        flex: 1,
        headerName: "Carbs",
        valueGetter: (params: any) => params.row.portions[0]?.carbs,
      },
      {
        field: "portions.fat",
        minWidth: 80,
        flex: 1,
        headerName: "Fat",
        valueGetter: (params: any) => params.row.portions[0]?.fat,
      },
      {
        field: "portions.protein",
        minWidth: 80,
        flex: 1,
        headerName: "Protein",
        valueGetter: (params: any) => params.row.portions[0]?.protein,
      },
      {
        field: "portionUnit",
        minWidth: 80,
        flex: 1,
        headerName: "Unit",
        sortable: false,
        valueGetter: (params: any) => params.row.portionUnit,
      },
    ];
  }, [meals]);

  useEffect(() => {
    const getMeals = async () => {
      setLoading(true);
      try {
        const res = await Api.AdminMeal.listMeals(
          filterModel.query,
          paginationModel.page,
          paginationModel.pageSize,
          sortModel.map((i) => `${i.field},${i.sort}`)
        );
        setMeals(res.data.content as AdminMealMultiLangResponse[]);
        setTotalRowCount(Number(res.data.totalElements));
      } catch (error) {}
      setLoading(false);
    };
    getMeals();
  }, [paginationModel, sortModel, retriggerReq, filterModel]);

  return (
    <Box className="meals">
      <Subheader page="meals" />
      <Box p={6} className="meals-container">
        <DataTable
          rows={meals}
          rowCount={totalRowCount}
          columns={columns}
          sortingMode="server"
          sortModel={sortModel}
          paginationMode="server"
          paginationModel={paginationModel}
          paginationChange={handlePaginationChange}
          sortChange={handleSortChange}
          filterModelChange={setFilterModel}
          loading={loading}
          create={create}
          edit={edit}
          deleteRec={deleteRec}
          options={{
            entityName: "meal",
            filter: true,
            permission: { create: "MEAL_CREATE", edit: "MEAL_EDIT", delete: "MEAL_DELETE" },
          }}
        />
      </Box>
      <MealCrud create open={createShow} title="Create New Meal" onClose={handleClose} onConfirm={handleConfirm} />
      <MealCrud
        edit
        open={editShow.show}
        meal={editShow.meal}
        title="Edit Meal"
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
      <ConfirmDialog />
    </Box>
  );
}
